import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./reports_tracker_not_registered.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import maleDefaultPic from "../../assets/male-profile-picture.png";
import femaleDefaultPic from "../../assets/female-profile-picture.png";
import closeIcon from "../../assets/close_icon.png";
import pureVegIcon from "../../assets/pure-veg-symbol.jpg";
import nonVegIcon from "../../assets/non-veg-symbol.jpg";
import * as XLSX from "xlsx";
import saveAs from "file-saver";
import AddUserModal from "./AddUserModal.tsx";

interface User {
  _id: string;
  name: string;
  phoneNumber: string;
  email: string;
  gender: string;
  age: string;
  weight: string;
  height: string;
  goal: string;
  activity: string;
  medicalConcern: string;
  conditions: object;
  startingBmi: string;
  languageCode: string;
  profilePicture: string;
  activePlans: any;
  historyPlans: any;
  weightEntries: object;
  dietPlan: object;
  createdAt: Date;
  updatedAt: Date;
  totalCalorieSearchApiCalls: number;
  activeCalorieTrackerBySearchPlan: any;
  historyCalorieTrackerBySearchPlans: any;
  dietaryType: string;
  lastOnline: Date;
  registrationDevice: string;
  goalWeightLoss: string;
  goalWeightGain: string;
  goalMuscleGain: string;
  goalHealthIssues: string;
  appliedCoupon: string;
  assignedTo?: string;
  comments?: string;
  clientStatus?: string;
  formSent: string;
}

const ReportsTrackerNotRegistered = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [expandedUsers, setExpandedUsers] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerm, setSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const isInitialRender = useRef(true);
  const [showSalesPersonModal, setShowSalesPersonModal] =
    useState<boolean>(false);
  const [selectedUserPhone, setSelectedUserPhone] = useState<string>("");
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<string>("");
  const [selectedFormStatus, setSelectedFormStatus] = useState<string>("");
  const [showFormStatusModal, setShowFormStatusModal] =
    useState<boolean>(false);
  const [isAssigning, setIsAssigning] = useState<boolean>(false);

  const [showClientsResponseModal, setShowClientsResponseModal] =
    useState<boolean>(false);
  const [showClientsDiseaseModal, setShowClientsDiseaseModal] =
    useState<boolean>(false);
  const [modalInputValue, setModalInputValue] = useState<any>("");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showClientStatusModal, setShowClientStatusModal] =
    useState<boolean>(false);
  const [selectedClientStatusFilter, setSelectedClientStatusFilter] =
    useState<string>("");

  const salesPersons = [
    "Saima",
    "Shabana",
    "Iram",
    "Neha",
    "Amaan",
    "Anurag",
    "N/A",
  ];

  const formStatusOptions = ["Yes", "No"];

  const clientStatusOptions = [
    "Interested/Follow up",
    "Discussed",
    "Not Interested",
    "Call Not Connected",
    "Call Not Received",
    "Call Received but Call Cut",
    "Blacklist",
    "Callback",
    "Junk Lead",
    "Subscribed",
    "Internal Team Member",
    "Applied by Mistake",
    "Invalid",
    "Interested in low budget",
    "Interested for 1 month",
  ];

  // State to manage the visibility of the modal
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Function to open the modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Function to close the modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Function to handle success (e.g., refresh data)
  const handleSuccess = () => {
    // You can re-fetch your user data here to show the new entry
    console.log("User data saved successfully! Refreshing data...");
    // Example: fetchUserData();
  };

  // Fetch user details and users
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllUsers();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      alert("Session expired or invalid token! Redirecting to login.");
      handleLogout();
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await axios.get(
        // `https://api.fitoja.in/api/get-all-users-info-not-registered`
        `https://api.fitoja.in/api/get-all-users-info`
      );
      setUsers(response.data.data.reverse());
    } catch (error) {
      console.error("Failed to fetch users:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(users);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

    // Convert to a binary Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "Employee_Details.xlsx");
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  // Expand or collapse individual user cards
  const toggleExpanded = (userId: string) => {
    setExpandedUsers((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const timeAgo = (dateString: string): string => {
    const now = new Date().getTime(); // Convert to timestamp
    const pastDate = new Date(dateString).getTime(); // Convert to timestamp
    const diffInSeconds = Math.floor((now - pastDate) / 1000);

    if (diffInSeconds < 60) {
      return `${diffInSeconds} sec ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)} min ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)} h ago`;
    } else if (diffInSeconds < 2592000) {
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    } else if (diffInSeconds < 31536000) {
      return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    } else {
      return `${Math.floor(diffInSeconds / 31536000)} years ago`;
    }
  };

  const openAssignSalesPersonModal = (phoneNumber: string) => {
    setSelectedUserPhone(phoneNumber);
    setSelectedSalesPerson("");
    setShowSalesPersonModal(true);
  };

  const openChangeFormStatusModal = (phoneNumber: string) => {
    setSelectedUserPhone(phoneNumber);
    setSelectedFormStatus("");
    setShowFormStatusModal(true);
  };

  const openChangeStatusModal = (phoneNumber: string) => {
    setSelectedUserPhone(phoneNumber);
    const currentUser = users.find((user) => user.phoneNumber === phoneNumber);
    setModalInputValue(currentUser?.clientStatus || "");
    setShowClientStatusModal(true);
  };

  const assignSalesPerson = async () => {
    if (!selectedSalesPerson) {
      alert("Please select a sales person");
      return;
    }

    setIsAssigning(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          assignedTo: selectedSalesPerson,
        },
        config
      );

      // Update the local state to reflect the change
      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, assignedTo: selectedSalesPerson }
            : user
        )
      );

      setShowSalesPersonModal(false);
    } catch (error) {
      console.error("Failed to assign sales person:", error);
      alert("Failed to assign sales person. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  };

  const changeFormStatusApi = async () => {
    if (!selectedFormStatus) {
      alert("Please select a sales person");
      return;
    }

    setIsAssigning(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          formSent: selectedFormStatus,
        },
        config
      );

      // Update the local state to reflect the change
      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, formSent: selectedFormStatus }
            : user
        )
      );

      setShowFormStatusModal(false);
    } catch (error) {
      console.error("Failed to assign sales person:", error);
      alert("Failed to assign sales person. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  };

  const handleUpdateClientStatus = async () => {
    if (!modalInputValue) {
      alert("Please select a status");
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          clientStatus: modalInputValue,
        },
        config
      );

      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, clientStatus: modalInputValue }
            : user
        )
      );

      setShowClientStatusModal(false);
    } catch (error) {
      console.error("Failed to update client status:", error);
      alert("Failed to update client status. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  function convertHeightToMeters(heightString: string): number {
    if (!heightString) return 0;

    if (heightString.includes("ft")) {
      const feetPattern = /(\d+)\s*ft/;
      const inchesPattern = /(\d+)\s*in/;

      const feetMatch = heightString.match(feetPattern);
      const inchesMatch = heightString.match(inchesPattern);

      const feet = feetMatch ? parseFloat(feetMatch[1]) : 0.0;
      const inches = inchesMatch ? parseFloat(inchesMatch[1]) : 0.0;

      const totalInches = feet * 12 + inches;
      return totalInches * 0.0254; // Convert inches to meters
    } else if (heightString.includes("cm")) {
      const valuePart = heightString.replace(/[^\d.]/g, "");
      const heightInCm = parseFloat(valuePart) || 0.0;
      return heightInCm / 100; // Convert cm to meters
    }
    return 0;
  }

  const calculateMinNormalWeightRange = (
    bmi: number,
    heightInMeter: number
  ) => {
    const result = bmi * heightInMeter * heightInMeter;
    return result;
  };

  const openAssignSalesPersonResponseModal = (
    phoneNumber: string,
    comments: string | undefined
  ) => {
    setSelectedUserPhone(phoneNumber);
    setModalInputValue(comments || "");
    setShowClientsResponseModal(true);
  };

  const openClientDiseaseModal = (
    phoneNumber: string,
    disease: object | undefined
  ) => {
    console.log("disease: ", disease);
    setSelectedUserPhone(phoneNumber);
    setModalInputValue(disease || {}); // Use an empty object as a fallback
    setShowClientsDiseaseModal(true);
  };

  const handleUpdateClientComments = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(
        "https://api.fitoja.in/api/edit-personal-info",
        {
          phoneNumber: selectedUserPhone,
          comments: modalInputValue,
        },
        config
      );

      setUsers(
        users.map((user) =>
          user.phoneNumber === selectedUserPhone
            ? { ...user, comments: modalInputValue }
            : user
        )
      );

      setShowClientsResponseModal(false);
    } catch (error) {
      console.error("Failed to save response:", error);
      alert("Failed to save response. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  const filteredUsers = users.filter((user) => {
    const matchesSearchTerm = user.phoneNumber.includes(searchTerm);
    const matchesClientStatus =
      selectedClientStatusFilter === "" ||
      user.clientStatus === selectedClientStatusFilter;

    return matchesSearchTerm && matchesClientStatus;
  });

  return (
    <div className="reports-tracker-not-registered ">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          {/* Sales Person Assignment Modal */}
          {showSalesPersonModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Assign a Person</h3>
                <p>Select a person for this user:</p>
                <select
                  value={selectedSalesPerson}
                  onChange={(e) => setSelectedSalesPerson(e.target.value)}
                  className="sales-person-select"
                >
                  <option value="">Select Sales Person</option>
                  {salesPersons.map((person) => (
                    <option key={person} value={person}>
                      {person}
                    </option>
                  ))}
                </select>
                <div className="modal-buttons">
                  <button
                    onClick={() => setShowSalesPersonModal(false)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={assignSalesPerson}
                    className="assign-button"
                    disabled={isAssigning || !selectedSalesPerson}
                  >
                    {isAssigning ? "Assigning..." : "Assign"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Change Form Status Modal */}
          {showFormStatusModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Change Form Status</h3>
                <select
                  value={selectedFormStatus}
                  onChange={(e) => setSelectedFormStatus(e.target.value)}
                  className="sales-person-select"
                >
                  <option value="">Change Sent Form Status</option>
                  {formStatusOptions.map((person) => (
                    <option key={person} value={person}>
                      {person}
                    </option>
                  ))}
                </select>
                <div className="modal-buttons">
                  <button
                    onClick={() => setShowFormStatusModal(false)}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={changeFormStatusApi}
                    className="assign-button"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Client's Disease Modal */}
          {showClientsDiseaseModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Diseases</h3>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  {Object.entries(modalInputValue || {})
                    .filter(([key, value]) => value === true)
                    .map(([key], index, array) => {
                      const diseaseNames = {
                        diabetes: "Diabetes",
                        menopause: "Menopause",
                        pcosPcod: "PCOS/PCOD",
                        cardiovascular: "Cardiovascular",
                        immunity: "Immunity",
                        hairAndSkin: "Hair & Skin",
                        highUricAcid: "High Uric Acid",
                        anemia: "Anemia",
                        ulcerativeColitis: "Ulcerative Colitis",
                        crohnsDisease: "Crohn's Disease",
                        sle: "SLE",
                        psoriasis: "Psoriasis",
                        hashimotos: "Hashimoto's",
                        irritableBowelSyndrome: "Irritable Bowel Syndrome",
                        thyroidism: "Thyroidism",
                        antiCancerDiets: "Anti-Cancer Diets",
                        otherMedicalConditions: (modalInputValue as any)
                          .otherMedicalConditionsDes,
                      };
                      const diseaseName = diseaseNames[key] || null;

                      return (
                        <span key={key}>
                          {diseaseName}
                          {/* Add separator if it's not the last item */}
                          {index < array.length - 1 && " | "}
                        </span>
                      );
                    })}
                </div>
                <div className="modal-actions">
                  <button
                    onClick={() => setShowClientsDiseaseModal(false)}
                    className="button button-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Client's Response Text Modal */}
          {showClientsResponseModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Edit Client Comments</h3>
                <textarea
                  value={modalInputValue}
                  rows={6}
                  onChange={(e) => setModalInputValue(e.target.value)}
                  placeholder="Enter comments here..."
                ></textarea>
                <div className="modal-actions">
                  <button
                    onClick={() => setShowClientsResponseModal(false)}
                    className="button button-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateClientComments}
                    className="button button-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Comments"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Client's Status Modal */}
          {showClientStatusModal && (
            <div className="modal-overlay">
              <div className="modal-content">
                <h3>Update Client Status</h3>
                <select
                  value={modalInputValue}
                  onChange={(e) => setModalInputValue(e.target.value)}
                  className="status-select"
                >
                  <option value="">Select Status</option>
                  {clientStatusOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <div className="modal-actions">
                  <button
                    onClick={() => setShowClientStatusModal(false)}
                    className="button button-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateClientStatus}
                    className="button button-primary"
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Status"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Search Box */}
          <div
            className="search-box"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <h2>Report Tracker (Registered Users)</h2>
            <div className="filters-and-search">
              <input
                type="text"
                placeholder="Search by Phone Number"
                style={{ marginLeft: "auto", marginRight: "auto" }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                value={selectedClientStatusFilter}
                onChange={(e) => setSelectedClientStatusFilter(e.target.value)}
                className="status-filter-select"
              >
                <option value="">All Client Status</option>
                {clientStatusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <span
              style={{
                marginLeft: "auto",
                marginRight: "15px",
                marginTop: "10px",
              }}
            >
              <button
                style={{
                  padding: "5px 15px",
                  fontSize: "18px",
                  cursor: "pointer",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "bold",
                  marginRight: "20px",
                }}
                onClick={() => {
                  exportToExcel();
                }}
              >
                Generate Report (.xlsx file)
              </button>

              {/* Button to open the modal */}
              {/* <button
                onClick={handleOpenModal}
                style={{
                  padding: "5px 15px",
                  fontSize: "18px",
                  cursor: "pointer",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontWeight: "bold",
                }}
              >
                Add New User
              </button> */}

              {/* Conditionally render the modal */}
              {isModalOpen && (
                <AddUserModal
                  onClose={handleCloseModal}
                  onSuccess={handleSuccess}
                />
              )}
            </span>
          </div>

          <div className="user-list">
            {filteredUsers.length > 0 ? (
              <table className="user-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Registered on</th>
                    <th>Assigned To</th>
                    <th>Assign a Person</th>
                    <th>Client's Status</th>
                    <th>Change Status</th>
                    <th>Comments</th>
                    <th>Gender</th>
                    <th>Age</th>
                    <th>Height</th>
                    <th>Weight</th>
                    <th>BMI</th>
                    <th>Normal Weight Should be:</th>
                    <th>Dietary Type</th>
                    <th>Goal</th>
                    <th>Activity</th>
                    <th>Disease</th>
                    <th>Currently Active Plan</th>
                    <th>History Plans</th>
                    <th>Calorie Search Count</th>
                    <th>Last Seen</th>
                    <th>Device</th>
                    <th>Form Sent</th>
                    <th>Change Form Sent Status</th>
                    <th>Email</th>
                    {/* Add more headings for 'more details' if always expanded */}
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr key={user._id} className="user-row">
                      <td>{index + 1}</td>
                      <td>{user.name}</td>
                      <td>{user.phoneNumber}</td>
                      <td>
                        <span>
                          {new Date(user.createdAt)
                            .toLocaleString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                              hour12: true,
                            })
                            .replace(",", "")
                            .replace(/(\d)(?=\d{2}\:)/, "$1 ")}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{ color: user.assignedTo ? "green" : "red" }}
                        >
                          {user.assignedTo || "Not Assigned"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="assign-sales-person-button"
                          onClick={() =>
                            openAssignSalesPersonModal(user.phoneNumber)
                          }
                        >
                          Assign a Person
                        </button>
                      </td>
                      <td>{user.clientStatus || "N/A"}</td>
                      <td>
                        <button
                          className="assign-sales-person-button"
                          onClick={() =>
                            openChangeStatusModal(user.phoneNumber)
                          }
                        >
                          Change Status
                        </button>
                      </td>
                      <td>
                        <button
                          className="assign-sales-person-button"
                          onClick={() =>
                            openAssignSalesPersonResponseModal(
                              user.phoneNumber,
                              user.comments
                            )
                          }
                        >
                          {user.comments ? "View" : "Add"}
                        </button>
                      </td>
                      <td>{user.gender}</td>
                      <td>{user.age}</td>
                      <td>{user.height}</td>
                      <td>{user.weight}</td>
                      <td>{parseFloat(user.startingBmi).toFixed(1)}</td>
                      <td>
                        <span>
                          {calculateMinNormalWeightRange(
                            18.5,
                            convertHeightToMeters(user.height)
                          ).toFixed(1)}{" "}
                          kg
                        </span>{" "}
                        -{" "}
                        <span>
                          {calculateMinNormalWeightRange(
                            24.9,
                            convertHeightToMeters(user.height)
                          ).toFixed(1)}{" "}
                          kg
                        </span>
                      </td>
                      <td>
                        {user.dietaryType === "Vegetarian" ? (
                          <div className="veg-container">
                            <img
                              src={pureVegIcon}
                              height="20px"
                              alt="Vegetarian"
                            />
                            <span>Vegetarian</span>
                          </div>
                        ) : (
                          <div className="nonveg-container">
                            <img
                              src={nonVegIcon}
                              height="20px"
                              alt="Non-Vegetarian"
                            />
                            <span>Non-Vegetarian</span>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "10px" }}>
                          {user.goalWeightLoss === "true" && (
                            <span>Weight Loss</span>
                          )}
                          {user.goalWeightGain === "true" && (
                            <span>Weight Gain</span>
                          )}
                          {user.goalMuscleGain === "true" && (
                            <span>Muscle Gain</span>
                          )}
                          {user.goalHealthIssues === "true" && (
                            <span>Other</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span>{user.activity}</span>
                      </td>
                      <td>
                        {user.medicalConcern == "Yes" ? (
                          <button
                            className="assign-sales-person-button"
                            onClick={() => {
                              openClientDiseaseModal(
                                user.phoneNumber,
                                user.conditions
                              );
                            }}
                          >
                            View
                          </button>
                        ) : (
                          "No"
                        )}
                      </td>
                      <td>
                        <p
                          style={{
                            display: "flex",
                            flexDirection: user.activePlans[0]?.planName
                              ? "column"
                              : "row",
                            alignItems: "baseline",
                            gap: "5px",
                          }}
                        >
                          {user.activePlans[0]?.planName ? (
                            <div className="history-plans-card">
                              {user.activePlans.map(
                                (plan: any, index: number) => (
                                  <span
                                    className="history-plans-card-item"
                                    key={index}
                                  >
                                    <span style={{ fontWeight: "500" }}>
                                      {index + 1 + ". " + plan.planName + ":"}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Price: ₹" + plan.price}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Start date: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.startDate))}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • End date: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.endDate))}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Coupon Code used (If any): " +
                                        (user.appliedCoupon != null
                                          ? user.appliedCoupon
                                          : "N/A")}
                                    </span>
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "red" }}>No</span>
                          )}
                        </p>
                      </td>
                      <td>
                        <p
                          style={{
                            display: "flex",
                            flexDirection: user.historyPlans[0]?.planName
                              ? "column"
                              : "row",
                            alignItems: "baseline",
                            gap: "5px",
                          }}
                        >
                          {user.historyPlans[0]?.planName ? (
                            <div className="history-plans-card">
                              {user.historyPlans.map(
                                (plan: any, index: number) => (
                                  <span
                                    className="history-plans-card-item"
                                    key={index}
                                  >
                                    <span style={{ fontWeight: "500" }}>
                                      {index + 1 + ". " + plan.planName + ":"}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Price: ₹" + plan.price}{" "}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Start date: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.startDate))}
                                    </span>
                                    <span style={{ paddingLeft: "10px" }}>
                                      {"  • Expired on: " +
                                        new Intl.DateTimeFormat("en-IN", {
                                          day: "2-digit",
                                          month: "short",
                                          year: "numeric",
                                          timeZone: "Asia/Kolkata",
                                        }).format(new Date(plan.endDate))}
                                    </span>
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            <span style={{ color: "red" }}>No</span>
                          )}
                        </p>
                      </td>
                      <td>
                        <span>{user.totalCalorieSearchApiCalls}</span>
                      </td>
                      <td>{timeAgo(user?.lastOnline.toString())}</td>
                      <td>{user?.registrationDevice}</td>
                      <td>
                        {user.formSent == "Yes" ? (
                          <span style={{ color: "Green" }}>Yes</span>
                        ) : (
                          <span style={{ color: "Red" }}>No</span>
                        )}
                      </td>
                      <td>
                        <button
                          className="assign-sales-person-button"
                          onClick={() =>
                            openChangeFormStatusModal(user.phoneNumber)
                          }
                        >
                          Change Form Status
                        </button>
                      </td>
                      <td>{user.email}</td>
                      {/* Add more cells for other data points like Activity, Plans, etc. if you want them in the default view */}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No User found.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsTrackerNotRegistered;
